/**
 * Configuración del pipeline offline session.analyze (Fase 41).
 *
 * Edita la fila singleton `analyze_config`. Cambios surten efecto en la
 * próxima sesión (no afectan jobs ya encolados). Patrón heredado de
 * `Billing/Config.tsx`.
 */
import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchAnalyzeConfig,
  fetchAnalyzeConfigAudit,
  patchAnalyzeConfig,
  type AnalyzeConfig,
  type AnalyzeConfigAuditEntry,
  type AutoTriageRule,
  type CriticStrictness,
} from '../../api/admin/analyze-config.js';
import { AdminApiError } from '../../api/admin/_core.js';

export function AnalyzeConfigPage(): ReactElement {
  const [tab, setTab] = useState<'edit' | 'audit'>('edit');
  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Analyze · Configuración</h1>
          <p className="page-sub">
            Gates de encolado, caps del worker (Reader/Critic) y filtros
            post-Critic. Cambios afectan sesiones nuevas — no re-encolan las
            que ya estaban en cola.
          </p>
        </div>
      </div>
      <div className="config-page">
        <section className="card">
          <header className="card-head config-tabs">
            <div className="segmented" role="tablist" aria-label="Vista">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'edit'}
                className={tab === 'edit' ? 'is-active' : ''}
                onClick={() => setTab('edit')}
              >
                Editar
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'audit'}
                className={tab === 'audit' ? 'is-active' : ''}
                onClick={() => setTab('audit')}
              >
                Historial
              </button>
            </div>
            <small>
              Cambios afectan <strong>sesiones nuevas</strong> · sin redeploy
            </small>
          </header>
          {tab === 'edit' ? <AnalyzeEditor /> : <AnalyzeAudit />}
        </section>
      </div>
    </>
  );
}

function AnalyzeEditor(): ReactElement {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'analyze-config'],
    queryFn: fetchAnalyzeConfig,
  });
  const [draft, setDraft] = useState<AnalyzeConfig | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const mutation = useMutation({
    mutationFn: (next: AnalyzeConfig) => patchAnalyzeConfig(next),
    onSuccess: () => {
      setDraft(null);
      setToast({ kind: 'ok', text: 'Configuración guardada' });
      void qc.invalidateQueries({ queryKey: ['admin', 'analyze-config'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'analyze-config-audit'] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof AdminApiError ? err.message : String(err);
      setToast({ kind: 'err', text: msg });
    },
  });

  if (query.isPending) return <div className="config-state">Cargando…</div>;
  if (query.isError) {
    return (
      <div className="config-state config-state-err">
        Error: {(query.error).message}
      </div>
    );
  }
  if (!query.data) return <div className="config-state">Sin datos</div>;

  const data = query.data;
  const effective: AnalyzeConfig = draft ?? data.config;
  const isDirty = draft !== null;
  const defaults = data.defaults;
  const modelOptions = data.modelOptions.map((m) => ({
    value: m.id,
    label: `${m.displayName} (${m.id})`,
  }));

  const patch = (changes: Partial<AnalyzeConfig>): void => {
    setDraft({ ...effective, ...changes });
  };

  const confirmKill = (): boolean => {
    if (!effective.enabled) return true;
    return window.confirm(
      'Vas a apagar el pipeline entero. Ninguna sesión nueva va a generar findings hasta que lo vuelvas a encender. ¿Confirmás?',
    );
  };

  return (
    <div className="config-editor">
      <div className="config-meta">
        <span className="config-meta-label">Última edición</span>
        <span className="config-meta-value mono">
          {data.updatedAt
            ? new Date(data.updatedAt).toLocaleString('es-AR', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })
            : 'nunca · usando defaults'}
        </span>
      </div>

      <FormGroup title="Kill-switch">
        <ToggleField
          id="cfg-enabled"
          label="Pipeline habilitado"
          value={effective.enabled}
          defaultValue={defaults.enabled}
          onChange={(v) => {
            if (!v && !confirmKill()) return;
            patch({ enabled: v });
          }}
          disabled={mutation.isPending}
        />
      </FormGroup>

      <FormGroup title="Gates de encolado">
        <NumberField
          id="cfg-min-turn"
          label="Min turn count"
          hint="Sesiones con menos turnos persistidos no se encolan."
          value={effective.minTurnCount}
          defaultValue={defaults.minTurnCount}
          min={0}
          max={1000}
          step={1}
          onChange={(v) => patch({ minTurnCount: v })}
          disabled={mutation.isPending}
        />
        <NumberField
          id="cfg-min-duration"
          label="Min duration (segundos)"
          hint="Sesiones más cortas se saltean."
          value={effective.minDurationSeconds}
          defaultValue={defaults.minDurationSeconds}
          min={0}
          max={86400}
          step={5}
          onChange={(v) => patch({ minDurationSeconds: v })}
          disabled={mutation.isPending}
        />
        <ToggleField
          id="cfg-analyze-synth"
          label="Analizar sesiones sintéticas"
          hint="Si OFF, las sesiones del simulator no se analizan (default)."
          value={effective.analyzeSynthetic}
          defaultValue={defaults.analyzeSynthetic}
          onChange={(v) => patch({ analyzeSynthetic: v })}
          disabled={mutation.isPending}
        />
        <NumberField
          id="cfg-sample-rate"
          label="Sample rate"
          hint="0..1. 1 = todas, 0.5 = ~mitad."
          value={effective.analyzeSampleRate}
          defaultValue={defaults.analyzeSampleRate}
          min={0}
          max={1}
          step={0.05}
          onChange={(v) => patch({ analyzeSampleRate: v })}
          disabled={mutation.isPending}
        />
      </FormGroup>

      <FormGroup title="Budget">
        <NumberField
          id="cfg-budget"
          label="Cap mensual (USD)"
          hint="Cuando el gasto del mes supera este número, el worker entra en modo skip y los runs se marcan timeout."
          value={effective.budgetMonthlyUsd}
          defaultValue={defaults.budgetMonthlyUsd}
          min={1}
          max={1000000}
          step={10}
          onChange={(v) => patch({ budgetMonthlyUsd: v })}
          disabled={mutation.isPending}
        />
      </FormGroup>

      <FormGroup title="Worker caps">
        <NumberField
          id="cfg-reader-max"
          label="Reader max iterations"
          hint="Cap del loop del Reader. 12 es el default histórico."
          value={effective.readerMaxIterations}
          defaultValue={defaults.readerMaxIterations}
          min={1}
          max={30}
          step={1}
          onChange={(v) => patch({ readerMaxIterations: v })}
          disabled={mutation.isPending}
        />
        <NumberField
          id="cfg-max-findings"
          label="Max findings por run"
          hint="Si el Reader propone más, el resto se descarta."
          value={effective.maxFindingsPerRun}
          defaultValue={defaults.maxFindingsPerRun}
          min={1}
          max={100}
          step={1}
          onChange={(v) => patch({ maxFindingsPerRun: v })}
          disabled={mutation.isPending}
        />
      </FormGroup>

      <FormGroup
        title="Modelos del pipeline"
        description={
          <>
            Uno por rol (Planner → Reader → Critic + matcher de regression).
            Sólo modelos anthropic del catálogo estático. El costo por run
            cambia fuerte con el modelo — revisá el cap mensual al subirlos.
          </>
        }
      >
        <SelectField
          id="cfg-model-planner"
          label="Planner model"
          value={effective.plannerModel}
          defaultValue={defaults.plannerModel}
          options={modelOptions}
          onChange={(v) => patch({ plannerModel: v })}
          disabled={mutation.isPending}
        />
        <SelectField
          id="cfg-model-reader"
          label="Reader model"
          value={effective.readerModel}
          defaultValue={defaults.readerModel}
          options={modelOptions}
          onChange={(v) => patch({ readerModel: v })}
          disabled={mutation.isPending}
        />
        <SelectField
          id="cfg-model-critic"
          label="Critic model"
          value={effective.criticModel}
          defaultValue={defaults.criticModel}
          options={modelOptions}
          onChange={(v) => patch({ criticModel: v })}
          disabled={mutation.isPending}
        />
        <SelectField
          id="cfg-model-matcher"
          label="Matcher model"
          hint="Fuzzy match de regressions — alcanza con el modelo más barato."
          value={effective.matcherModel}
          defaultValue={defaults.matcherModel}
          options={modelOptions}
          onChange={(v) => patch({ matcherModel: v })}
          disabled={mutation.isPending}
        />
      </FormGroup>

      <FormGroup title="Critic / Matcher">
        <SelectField
          id="cfg-critic"
          label="Critic strictness"
          hint="Variación del system prompt del Critic. STRICT = sesgo histórico de la Fase 38."
          value={effective.criticStrictness}
          defaultValue={defaults.criticStrictness}
          options={[
            { value: 'strict', label: 'strict (rechaza por default)' },
            { value: 'balanced', label: 'balanced (acepta findings sólidos)' },
            { value: 'permissive', label: 'permissive (rechaza sólo lo obvio)' },
          ]}
          onChange={(v) => patch({ criticStrictness: v as CriticStrictness })}
          disabled={mutation.isPending}
        />
        <ToggleField
          id="cfg-matcher"
          label="Matcher de regression habilitado"
          hint="Cuando OFF, no se chequea si los findings ya estaban en analyze_eval_dataset."
          value={effective.matcherEnabled}
          defaultValue={defaults.matcherEnabled}
          onChange={(v) => patch({ matcherEnabled: v })}
          disabled={mutation.isPending}
        />
      </FormGroup>

      <FormGroup
        title="Muted categories"
        description={
          <>
            Findings con estas categorías se rechazan automáticamente
            post-Critic. El run row registra{' '}
            <code>findings_proposed &gt; findings_accepted</code>.
          </>
        }
      >
        <div className="config-field">
          <StringListEditor
            values={effective.mutedCategories}
            placeholder="pricing_outlier"
            onChange={(next) => patch({ mutedCategories: next })}
            disabled={mutation.isPending}
          />
        </div>
      </FormGroup>

      <FormGroup
        title="Auto-triage rules"
        description={
          <>
            Reglas evaluadas en orden — la primera que matchea gana. Se aplican
            post-Critic, post-mute, antes de persistir. Acción{' '}
            <code>reject</code> deja el finding con{' '}
            <code>status = rejected</code> +{' '}
            <code>reviewer_notes = "auto: …"</code>.
          </>
        }
      >
        <div className="config-field">
          <AutoTriageRulesEditor
            rules={effective.autoTriageRules}
            onChange={(next) => patch({ autoTriageRules: next })}
            disabled={mutation.isPending}
          />
        </div>
      </FormGroup>

      <footer className="config-footer">
        <div className="config-footer-left">
          {isDirty && (
            <span className="config-dirty-pill">
              <span className="config-dirty-dot" aria-hidden="true" />
              Hay cambios sin guardar
            </span>
          )}
          {toast && (
            <span
              className={toast.kind === 'ok' ? 'config-toast ok' : 'config-toast err'}
              role="status"
            >
              {toast.text}
            </span>
          )}
        </div>
        <div className="config-footer-actions">
          {isDirty && (
            <button
              type="button"
              className="btn ghost"
              onClick={() => setDraft(null)}
              disabled={mutation.isPending}
            >
              Descartar
            </button>
          )}
          <button
            type="button"
            className="btn ghost"
            disabled={mutation.isPending}
            onClick={() => setDraft({ ...defaults })}
            title="Carga los defaults del schema (no los guarda)"
          >
            Reset a defaults
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!isDirty || mutation.isPending}
            onClick={() => draft && mutation.mutate(draft)}
          >
            {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </footer>
    </div>
  );
}

function FormGroup({
  title,
  description,
  children,
}: {
  title: string;
  description?: ReactElement | string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <section className="config-group">
      <header className="config-group-head">
        <h3 className="config-group-title">{title}</h3>
        {description && <p className="config-group-desc">{description}</p>}
      </header>
      <div className="config-group-body">{children}</div>
    </section>
  );
}

interface ToggleFieldProps {
  id: string;
  label: string;
  hint?: string;
  value: boolean;
  defaultValue?: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}
function ToggleField(props: ToggleFieldProps): ReactElement {
  const overridden = props.defaultValue !== undefined && props.defaultValue !== props.value;
  return (
    <div className="config-field">
      <label className="config-field-label" htmlFor={props.id}>
        {props.label}
        {overridden && <span className="config-field-mark"> · custom</span>}
      </label>
      <div className="config-field-row">
        <input
          id={props.id}
          type="checkbox"
          checked={props.value}
          onChange={(e) => props.onChange(e.target.checked)}
          disabled={props.disabled}
        />
        <span className="mono t-meta">{props.value ? 'ON' : 'OFF'}</span>
      </div>
      {props.hint && <p className="config-field-hint">{props.hint}</p>}
    </div>
  );
}

interface NumberFieldProps {
  id: string;
  label: string;
  hint?: string;
  value: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}
function NumberField(props: NumberFieldProps): ReactElement {
  const overridden = props.defaultValue !== undefined && props.defaultValue !== props.value;
  return (
    <div className="config-field">
      <label className="config-field-label" htmlFor={props.id}>
        {props.label}
        {overridden && <span className="config-field-mark"> · custom</span>}
      </label>
      <div className="config-field-row">
        <input
          id={props.id}
          type="number"
          className="config-input"
          value={props.value}
          {...(props.min !== undefined ? { min: props.min } : {})}
          {...(props.max !== undefined ? { max: props.max } : {})}
          {...(props.step !== undefined ? { step: props.step } : {})}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v)) props.onChange(v);
          }}
          disabled={props.disabled}
        />
      </div>
      {props.hint && <p className="config-field-hint">{props.hint}</p>}
    </div>
  );
}

interface SelectFieldProps {
  id: string;
  label: string;
  hint?: string;
  value: string;
  defaultValue?: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  disabled?: boolean;
}
function SelectField(props: SelectFieldProps): ReactElement {
  const overridden = props.defaultValue !== undefined && props.defaultValue !== props.value;
  return (
    <div className="config-field">
      <label className="config-field-label" htmlFor={props.id}>
        {props.label}
        {overridden && <span className="config-field-mark"> · custom</span>}
      </label>
      <select
        id={props.id}
        className="config-input"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        disabled={props.disabled}
      >
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {props.hint && <p className="config-field-hint">{props.hint}</p>}
    </div>
  );
}

function StringListEditor({
  values,
  placeholder,
  onChange,
  disabled,
}: {
  values: string[];
  placeholder?: string;
  onChange: (next: string[]) => void;
  disabled?: boolean;
}): ReactElement {
  const [input, setInput] = useState('');
  const add = (): void => {
    const trimmed = input.trim();
    if (!trimmed) return;
    if (values.includes(trimmed)) {
      setInput('');
      return;
    }
    onChange([...values, trimmed]);
    setInput('');
  };
  return (
    <div className="chip-editor">
      <div className="chip-list">
        {values.length === 0 && <span className="t-meta">— vacío —</span>}
        {values.map((v) => (
          <span key={v} className="chip">
            {v}
            <button
              type="button"
              className="chip-x"
              onClick={() => onChange(values.filter((x) => x !== v))}
              aria-label={`Quitar ${v}`}
              disabled={disabled}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
      <div className="chip-add">
        <input
          type="text"
          className="config-input"
          {...(placeholder ? { placeholder } : {})}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          disabled={disabled}
        />
        <button
          type="button"
          className="btn btn-sm"
          onClick={add}
          disabled={disabled || !input.trim()}
        >
          Agregar
        </button>
      </div>
    </div>
  );
}

function AutoTriageRulesEditor({
  rules,
  onChange,
  disabled,
}: {
  rules: AutoTriageRule[];
  onChange: (next: AutoTriageRule[]) => void;
  disabled?: boolean;
}): ReactElement {
  const update = (i: number, patch: Partial<AutoTriageRule>): void => {
    const next = rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange(next);
  };
  const updateUnset = (
    i: number,
    key: keyof AutoTriageRule,
    value: string | number | undefined,
  ): void => {
    const next = rules.map((r, idx) => {
      if (idx !== i) return r;
      const copy = { ...r } as unknown as Record<string, unknown>;
      if (value === undefined || value === '' || value === null) {
        delete copy[key];
      } else {
        copy[key] = value;
      }
      return copy as unknown as AutoTriageRule;
    });
    onChange(next);
  };
  const remove = (i: number): void => {
    onChange(rules.filter((_, idx) => idx !== i));
  };
  const add = (): void => {
    onChange([...rules, { action: 'reject' }]);
  };
  return (
    <div className="triage-rules">
      {rules.length === 0 && (
        <span className="t-meta">— sin reglas — todos los findings van al inbox —</span>
      )}
      {rules.map((r, i) => (
        <div key={i} className="triage-rule">
          <input
            type="text"
            className="config-input"
            placeholder="category (opcional)"
            value={r.category ?? ''}
            onChange={(e) => updateUnset(i, 'category', e.target.value.trim() || undefined)}
            disabled={disabled}
          />
          <input
            type="number"
            className="config-input"
            placeholder="maxSev"
            min={1}
            max={5}
            value={r.maxSeverity ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (!raw) return updateUnset(i, 'maxSeverity', undefined);
              const v = Number(raw);
              if (!Number.isFinite(v) || v < 1 || v > 5) return;
              updateUnset(i, 'maxSeverity', v);
            }}
            disabled={disabled}
          />
          <input
            type="text"
            className="config-input"
            placeholder="module (opcional)"
            value={r.module ?? ''}
            onChange={(e) => updateUnset(i, 'module', e.target.value.trim() || undefined)}
            disabled={disabled}
          />
          <select
            className="config-input"
            value={r.action}
            onChange={(e) =>
              update(i, { action: e.target.value as 'reject' | 'duplicate' })
            }
            disabled={disabled}
          >
            <option value="reject">reject</option>
            <option value="duplicate">duplicate</option>
          </select>
          <input
            type="text"
            className="config-input"
            placeholder="reason"
            value={r.reason ?? ''}
            onChange={(e) => updateUnset(i, 'reason', e.target.value || undefined)}
            disabled={disabled}
          />
          <button
            type="button"
            className="btn btn-sm ghost"
            onClick={() => remove(i)}
            disabled={disabled}
            aria-label="Quitar regla"
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-sm"
        onClick={add}
        disabled={disabled}
      >
        + Agregar regla
      </button>
    </div>
  );
}

function AnalyzeAudit(): ReactElement {
  const query = useQuery({
    queryKey: ['admin', 'analyze-config-audit'],
    queryFn: fetchAnalyzeConfigAudit,
  });
  if (query.isPending) return <div className="config-state">Cargando…</div>;
  if (query.isError) {
    return (
      <div className="config-state config-state-err">
        Error: {(query.error).message}
      </div>
    );
  }
  const entries = query.data?.entries ?? [];
  if (entries.length === 0) {
    return (
      <div className="config-state">
        Sin cambios registrados. El primer PATCH va a aparecer acá.
      </div>
    );
  }
  return (
    <div className="config-audit">
      {entries.map((entry) => (
        <AnalyzeAuditCard key={entry.id} entry={entry} />
      ))}
    </div>
  );
}

function AnalyzeAuditCard({ entry }: { entry: AnalyzeConfigAuditEntry }): ReactElement {
  const when = new Date(entry.changedAt).toLocaleString('es-AR', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });
  return (
    <article className="audit-entry">
      <header className="audit-entry-head">
        <span className="audit-entry-when">{when}</span>
        <span className="audit-entry-by mono">
          por <code>{entry.changedByUserId?.slice(0, 8) ?? '—'}</code>
        </span>
      </header>
      <div className="audit-entry-diff">
        <div className="audit-side">
          <span className="audit-side-label">Antes</span>
          <pre className="audit-json">
            {entry.beforeConfig
              ? JSON.stringify(entry.beforeConfig, null, 2)
              : '— (sin row previa)'}
          </pre>
        </div>
        <div className="audit-side">
          <span className="audit-side-label">Después</span>
          <pre className="audit-json">{JSON.stringify(entry.afterConfig, null, 2)}</pre>
        </div>
      </div>
    </article>
  );
}
